const SERVERS = {
  local: 'http://devlocal.evolveapi.e-bode.com:8110',
  stage: 'https://evolveapidev.e-bode.com',
  production: 'https://rator.virtualunderwriter.net'
};

class AuthService {
  constructor() {
    this.token = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.currentServer = localStorage.getItem('selected_server') || 'stage';
    this.user = null;
    
    // Загрузить информацию о пользователе при инициализации если есть токен
    if (this.token) {
      this.loadUserInfo();
    }
  }

  getServers() {
    return SERVERS;
  }

  getCurrentServer() {
    return SERVERS[this.currentServer] || SERVERS.local;
  }

  setCurrentServer(serverKey) {
    if (SERVERS[serverKey]) {
      this.currentServer = serverKey;
      localStorage.setItem('selected_server', serverKey);
      // При смене сервера очищаем токены
      this.clearTokens();
    }
  }

  async login(username, password) {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(`${this.getCurrentServer()}/api/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    
    if (data.data && data.data.access_token) {
      this.setTokens(data.data.access_token, data.data.refresh_token);
      await this.loadUserInfo();
      return data.data;
    } else {
      throw new Error('Invalid response from server');
    }
  }

  async logout() {
    try {
      await fetch(`${this.getCurrentServer()}/api/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      // Восстанавливать сессию нечем. Чистим токены, иначе isAuthenticated()
      // останется true и редирект на /login отбросит обратно на главную.
      this.clearTokens();
      throw new Error('No refresh token available');
    }

    // cbsecurity ждёт refresh-токен в собственном заголовке (customRefreshHeader
    // в config/modules/cbsecurity.cfc). Authorization: Bearer он не смотрит и
    // отвечает 200 с error: true — «Cannot refresh the unrefreshable!».
    const response = await fetch(`${this.getCurrentServer()}/cbsecurity/refreshtoken`, {
      method: 'POST',
      headers: {
        'x-refresh-token': this.refreshToken
      }
    });

    // Эндпоинт отдаёт 200 и при отказе, поэтому одного статуса мало — читаем тело.
    let data = null;
    if (response.ok) {
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
    }

    const accessToken = data?.data?.access_token;
    if (!response.ok || !accessToken || data.error) {
      // Любой неуспех обязан очистить токены: с живым access_token в localStorage
      // пользователь попадает в петлю «страница -> /login -> главная».
      this.clearTokens();
      throw new Error(data?.messages?.[0] || 'Token refresh failed');
    }

    this.setTokens(accessToken, data.data.refresh_token);
    return accessToken;
  }

  setTokens(accessToken, refreshToken) {
    this.token = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    // Отправляем событие об изменении авторизации
    window.dispatchEvent(new Event('auth-change'));
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_info');
    // Отправляем событие об изменении авторизации
    window.dispatchEvent(new Event('auth-change'));
  }

  getToken() {
    return this.token;
  }

  isAuthenticated() {
    return !!this.token;
  }

  async makeAuthenticatedRequest(url, options = {}) {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${this.token}`
    };

    let response = await fetch(url, { ...options, headers });

    // If we get a 401, try to refresh the token
    if (response.status === 401 && this.refreshToken) {
      try {
        await this.refreshAccessToken();
        // Retry the request with the new token
        headers.Authorization = `Bearer ${this.token}`;
        response = await fetch(url, { ...options, headers });
      } catch (refreshError) {
        // Refresh failed: clear the tokens first, otherwise /login bounces back
        // to the home page because isAuthenticated() is still true.
        this.clearTokens();
        window.location.href = '/login';
        throw refreshError;
      }
    }

    return response;
  }

  async loadUserInfo() {
    try {
      const response = await fetch(`${this.getCurrentServer()}/api/whoami`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.user = data.data;
        localStorage.setItem('user_info', JSON.stringify(this.user));
      }
    } catch (error) {
      console.error('Failed to load user info:', error);
    }
  }

  getUser() {
    if (!this.user) {
      const savedUser = localStorage.getItem('user_info');
      if (savedUser) {
        this.user = JSON.parse(savedUser);
      }
    }
    return this.user;
  }

  hasAccess(section) {
    const user = this.getUser();
    if (!user || !user.accessOnly || !Array.isArray(user.accessOnly)) {
      return false;
    }
    return user.accessOnly.includes(section);
  }

  getAccessibleSections() {
    const user = this.getUser();
    if (!user || !user.accessOnly || !Array.isArray(user.accessOnly)) {
      return [];
    }
    return user.accessOnly;
  }
}

export const authService = new AuthService();